import { useEffect, useState, type ComponentType } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAdminStore } from '../lib/store';
import { useAuthStore } from '../lib/auth';
import clsx from 'clsx';
import {
  IconDashboard,
  IconCalendar,
  IconPlusCircle,
  IconClipboard,
  IconSettings,
  IconLogout,
  IconMenu,
  IconX,
} from './Icons';

type NavItem = {
  path: string;
  label: string;
  Icon: ComponentType<{ size?: number; className?: string }>;
  badge?: 'events' | 'registrations';
};

const navSections: { section: string | null; items: NavItem[] }[] = [
  { section: null, items: [
    { path: '/', label: '總覽', Icon: IconDashboard },
  ]},
  { section: '活動管理', items: [
    { path: '/events', label: '活動列表', Icon: IconCalendar, badge: 'events' },
    { path: '/events/new', label: '新增活動', Icon: IconPlusCircle },
  ]},
  { section: '報名管理', items: [
    { path: '/registrations', label: '報名列表', Icon: IconClipboard, badge: 'registrations' },
  ]},
  { section: '系統', items: [
    { path: '/settings', label: '設定', Icon: IconSettings },
  ]},
];

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { events, stats } = useAdminStore();
  const { user, logout } = useAuthStore();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close the drawer whenever the route changes (tap a link, use back button).
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Lock page scroll while the drawer is open, and close on Escape.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initial = (user?.displayName?.[0] || user?.username?.[0] || 'A').toUpperCase();

  return (
    <div className="flex min-h-screen bg-ground text-ink">
      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 bg-white border-b border-line flex items-center gap-2 px-3">
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label="開啟選單"
          aria-expanded={menuOpen}
          aria-controls="admin-sidebar"
          className="h-10 w-10 -ml-1 rounded-md text-ink hover:bg-sidebar-hover flex items-center justify-center"
        >
          <IconMenu size={22} />
        </button>
        <img src="/logo.png" alt="" width={32} height={32} className="w-8 h-8 shrink-0 object-contain" />
        <span className="text-[15px] font-bold">喵喵團報</span>
      </header>

      {/* Drawer backdrop */}
      <div
        aria-hidden="true"
        onClick={() => setMenuOpen(false)}
        className={clsx(
          'lg:hidden fixed inset-0 z-40 bg-[#0F1826]/50 transition-opacity',
          menuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
      />

      {/* Sidebar: fixed on desktop, slide-in drawer on smaller screens */}
      <aside
        id="admin-sidebar"
        className={clsx(
          'w-[272px] max-w-[85vw] lg:w-60 lg:max-w-none bg-white border-r border-line fixed top-0 left-0 bottom-0 z-50 flex flex-col',
          'transition-transform duration-200 ease-out lg:transition-none lg:translate-x-0',
          menuOpen ? 'translate-x-0 shadow-[0_0_40px_rgba(15,24,38,0.25)]' : '-translate-x-full',
        )}
      >
        {/* Brand */}
        <div className="flex items-center gap-2.5 px-5 pt-5 pb-4 border-b border-line-soft">
          <img src="/logo.png" alt="" width={40} height={40} className="w-10 h-10 shrink-0 object-contain" />
          <div className="flex flex-col leading-tight flex-1 min-w-0">
            <span className="text-[15px] font-bold">喵喵團報</span>
            <span className="text-xs text-muted">管理後台</span>
          </div>
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            aria-label="關閉選單"
            className="lg:hidden h-9 w-9 rounded-md text-muted hover:text-ink hover:bg-sidebar-hover flex items-center justify-center"
          >
            <IconX size={18} />
          </button>
        </div>

        {/* Navigation */}
        <nav aria-label="主選單" className="flex-1 px-3 py-4 overflow-y-auto flex flex-col gap-5">
          {navSections.map((section, i) => (
            <div key={section.section ?? i} className="flex flex-col gap-0.5">
              {section.section && (
                <div className="px-3 pb-1.5 text-xs text-[#8A94A3]">{section.section}</div>
              )}
              {section.items.map((item) => {
                const count =
                  item.badge === 'events' ? events.length :
                  item.badge === 'registrations' ? stats.totalRegistrations : 0;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/'}
                    className={({ isActive }) => clsx(
                      'flex items-center gap-2.5 h-11 lg:h-10 px-3 rounded-md text-sm transition-colors',
                      isActive
                        ? 'bg-primary-light text-primary font-medium'
                        : 'text-ink hover:bg-sidebar-hover'
                    )}
                  >
                    <item.Icon size={18} className="shrink-0" />
                    <span className="flex-1">{item.label}</span>
                    {item.badge && count > 0 && (
                      <span className={clsx(
                        'tabular text-xs rounded-full px-2 py-px',
                        item.badge === 'registrations'
                          ? 'bg-warn-soft text-warn'
                          : 'bg-line-soft text-muted'
                      )}>
                        {count}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        {/* User */}
        <div className="flex items-center gap-2.5 px-5 py-4 border-t border-line-soft pb-[max(1rem,env(safe-area-inset-bottom))]">
          <div className="w-[34px] h-[34px] rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold shrink-0">
            {initial}
          </div>
          <div className="flex-1 min-w-0 flex flex-col leading-snug">
            <span className="text-sm font-medium truncate">
              {user?.displayName || user?.username || 'Admin'}
            </span>
            <span className="text-xs text-muted truncate">
              {user?.username || user?.role || 'admin'}
            </span>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            aria-label="登出"
            title="登出"
            className="h-8 w-8 border border-line rounded-md bg-white text-muted hover:text-ink hover:bg-sidebar-hover flex items-center justify-center transition-colors"
          >
            <IconLogout size={18} />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-60 pt-14 lg:pt-0 min-h-screen min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
